import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BottomNavigation } from '../components/BottomNavigation';
import { 
  ArrowLeft,
  Star,
  Heart,
  Plus,
  User,
  Location,
  Calendar,
  Edit,
  TickSquare,
  Delete
} from 'react-iconly';
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, arrayUnion, arrayRemove, writeBatch } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { uploadShopCoverPhoto, uploadProductImage } from '../utils/imageUpload';
import { trackStoreView, generateSessionId } from '../lib/analyticsService';
import { BackButton, SortableList } from '../components/ui';
import { StoreRating } from '../components/ui/StoreRating';
import { ProductReviewsDrawer } from '../components/ui/ProductReviewsDrawer';
import { useShopStore, useAppStore } from '../lib/stores';
import { useShopBySeller, useProductsBySeller, useAvailableProductsBySeller, useReorderProducts } from '../lib/hooks';
import { productToasts, shopToasts, showError } from '../lib/utils/toast';
import { getStoreRating, getProductReviews } from '../lib/reviewsService';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const WARNING_ORANGE = '#FF9800';
const ERROR_RED = '#F44336';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};
const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

// Dummy shop data
const dummyShop = {
  id: 1,
  name: "Chef Mario's Kitchen",
  description: 'Authentic Italian cuisine made with love and fresh ingredients. Specializing in handmade pasta and traditional recipes passed down through generations.',
  profileImage: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400&h=400&fit=crop',
  rating: 4.8,
  reviews: 156,
  followers: 342,
  location: 'Downtown',
  deliveryTime: '30-45 min',
  isFavorited: false,
  badges: ['Top Rated', 'Local Favorite']
};

// Dummy shop products
const dummyProducts = [
  {
    id: 1,
    name: 'Handmade Pasta Carbonara',
    price: 18.99,
    rating: 4.8,
    reviews: 124,
    image: 'https://images.unsplash.com/photo-1621996346565-e3dbc353d2e5?w=400&h=300&fit=crop',
    category: 'Ready Now',
    cuisine: 'Italian',
    dietary: ['Vegetarian'],
    allergens: ['Egg', 'Dairy', 'Gluten'],
    available: true
  },
  {
    id: 2,
    name: 'Truffle Mushroom Risotto',
    price: 22.99,
    rating: 4.9,
    reviews: 89,
    image: 'https://images.unsplash.com/photo-1476124369491-e7addf5db371?w=400&h=300&fit=crop',
    category: 'Ready Now',
    cuisine: 'Italian',
    dietary: ['Vegetarian', 'Vegan'],
    allergens: ['Dairy'],
    available: true
  },
  {
    id: 3,
    name: 'Tiramisu Classico',
    price: 12.99,
    rating: 4.7,
    reviews: 67,
    image: 'https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?w=400&h=300&fit=crop',
    category: 'Desserts',
    cuisine: 'Italian',
    dietary: ['Vegetarian'],
    allergens: ['Egg', 'Dairy', 'Gluten'],
    available: true
  },
  {
    id: 4,
    name: 'Bruschetta al Pomodoro',
    price: 8.99,
    rating: 4.6,
    reviews: 45,
    image: 'https://images.unsplash.com/photo-1572441713131-4d09e2c54e6c?w=400&h=300&fit=crop',
    category: 'Appetizers',
    cuisine: 'Italian',
    dietary: ['Vegetarian', 'Vegan'],
    allergens: ['Gluten'],
    available: true
  },
  {
    id: 5,
    name: 'Osso Buco alla Milanese',
    price: 28.99,
    rating: 4.9,
    reviews: 78,
    image: 'https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=400&h=300&fit=crop',
    category: 'Ready Now',
    cuisine: 'Italian',
    dietary: [],
    allergens: ['Gluten'],
    available: false
  },
  {
    id: 6,
    name: 'Panna Cotta',
    price: 9.99,
    rating: 4.8,
    reviews: 56,
    image: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?w=400&h=300&fit=crop',
    category: 'Desserts',
    cuisine: 'Italian',
    dietary: ['Vegetarian'],
    allergens: ['Dairy', 'Gluten'],
    available: true
  }
];

// Feature flag for video feed experience
const USE_VIDEO_FEED = false;

// Enhanced video feed component using @dnd-kit
function ProductFeedView({ products, editMode, sellerId, onReorder, onProductClick, onVideoPlay, onVideoPause, onAddToCart, getCartQty, updateCartItem, removeCartItem, addStatus, videoRefs, currentPlayingIndex, onOpenReviews }) {
  // Only include products with videoUrl or imageUrl
  const feed = products.filter(p => p.videoUrl || p.imageUrl);

  if (!feed.length) {
    return <div style={{ color: '#fff', background: '#000', width: '100%', height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>No videos or images to display.</div>;
  }

  const handleReorder = (newOrder) => {
    console.log('[ProductFeedView] Products reordered:', newOrder);
    onReorder?.(newOrder);
  };

  const renderProductCard = (product, index) => (
    <div
      key={product.id}
      style={{
        position: 'relative',
        borderRadius: 0,
        overflow: 'hidden',
        background: '#fff',
        cursor: 'pointer',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 1px 4px #0001'
      }}
      tabIndex={0}
      aria-label={`View details for ${product.name}`}
      onClick={() => {
        console.log('[ProductFeedView] Product card clicked:', product.id);
        onProductClick(product.id);
      }}
    >
      {/* Product Image/Video - Full Width */}
      <div style={{ 
        position: 'relative', 
        width: '100%',
        aspectRatio: '9/16',
        background: '#000',
        flex: 'none'
      }}>
        {/* Video Player */}
        <video
          ref={(el) => {
            if (el) {
              const productIndex = products.findIndex(p => p.id === product.id);
              if (productIndex !== -1) {
                // Direct assignment to prevent infinite loops
                videoRefs[productIndex] = el;
              }
            }
          }}
          src={product.videoUrl || product.imageUrl || '/default-product.png'}
          poster={product.imageUrl || '/default-product.png'}
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover',
            backgroundColor: '#000',
            opacity: currentPlayingIndex === products.findIndex(p => p.id === product.id) ? '1' : '0.7'
          }}
          muted
          playsInline
          preload="metadata"
          onLoadedData={(e) => {
            // Set opacity when video is loaded
            if (product.videoUrl) {
              e.target.style.opacity = '1';
            }
          }}
        />
        
        {/* Play Button Overlay - Show for non-video items or when video is not playing */}
        {(!product.videoUrl || currentPlayingIndex !== products.findIndex(p => p.id === product.id)) && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(0,0,0,0.6)',
            borderRadius: '50%',
            width: 30,
            height: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none'
              }}>
            <div style={{
              width: 0,
              height: 0,
              borderLeft: '8px solid white',
              borderTop: '5px solid transparent',
              borderBottom: '5px solid transparent',
              marginLeft: 2
            }} />
          </div>
        )}
        
        {/* Availability Chip */}
        <div
                                  style={{
                          position: 'absolute',
                          top: '20px',
                          right: '20px',
                          zIndex: 1000,
                          background: 'rgba(255, 255, 255, 0.7)',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          fontSize: '8px',
                          fontWeight: 500,
                          color: '#333',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                          backdropFilter: 'blur(4px)',
                          border: 'none',
                          pointerEvents: 'none',
                        }}
        >
          {(() => {
            // Calculate next available day based on product inventory
            const today = new Date();
            const todayString = today.toISOString().split('T')[0];
            
            // Use inventory data from the product object
            if (product.inventory) {
              const inventory = product.inventory;
              
              // Check if same-day orders are allowed
              const sameDayAllowed = inventory.policy?.same_day_allowed || false;
              
              // Check if today has available inventory
              const todayInventory = inventory.scheduled?.find(row => 
                row.service_date === todayString && row.enabled && row.available_total > 0
              );
              
              // Also check for dates within 1 day of today (timezone handling)
              const todayPlusOne = new Date(today);
              todayPlusOne.setDate(today.getDate() + 1);
              const tomorrowString = todayPlusOne.toISOString().split('T')[0];
              
              const todayPlusOneInventory = inventory.scheduled?.find(row => 
                row.service_date === tomorrowString && row.enabled && row.available_total > 0
              );
              
              // Check if same-day orders are actually possible considering min_order_ahead policy
              const policy = product.policy;
              const canOrderToday = sameDayAllowed && 
                (todayInventory || todayPlusOneInventory) &&
                (!policy?.min_order_ahead || policy.min_order_ahead.value === 0);
              
              if (canOrderToday) {
                // Show "Available today" only if same-day orders are allowed, inventory is available, AND no min_order_ahead restriction
                return 'Available today';
              }
              
              // Find the next available day with inventory
              if (inventory.scheduled && Array.isArray(inventory.scheduled)) {
                const todayString = today.toISOString().split('T')[0];
                const policy = product.policy;
                
                const nextAvailableDay = inventory.scheduled
                  .filter(row => {
                    // Check if date has available units
                    if (row.available_total <= 0) return false;
                    
                    // Check if date is in the future
                    if (row.service_date <= todayString) return false;
                    
                    // Check policy restrictions
                    if (policy) {
                      const orderDate = new Date(row.service_date);
                      const daysDiff = Math.ceil((orderDate - today) / (1000 * 60 * 60 * 24));
                      
                      // Check min_order_ahead policy
                      if (policy.min_order_ahead && policy.min_order_ahead.value) {
                        const minDays = policy.min_order_ahead.value;
                        if (daysDiff < minDays) return false;
                      }
                      
                      // Check same_day_allowed policy
                      if (!policy.same_day_allowed && daysDiff === 0) return false;
                    }
                    
                    return true;
                  })
                  .sort((a, b) => new Date(a.service_date) - new Date(b.service_date))
                  .find(row => new Date(row.service_date) > today);
                
                if (nextAvailableDay) {
                  const nextDate = new Date(nextAvailableDay.service_date);
                  const options = { weekday: 'short', month: 'short', day: 'numeric' };
                  const formattedDate = nextDate.toLocaleDateString('en-US', options);
                  return `Available: ${formattedDate}`;
                }
              }
            }
            
            // Fallback: show tomorrow's date
            const tomorrow = new Date(today);
            tomorrow.setDate(today.getDate() + 1);
            const options = { weekday: 'short', month: 'short', day: 'numeric' };
            const nextAvailableDay = tomorrow.toLocaleDateString('en-US', options);
            
            return `Available: ${nextAvailableDay}`;
          })()}
        </div>
      </div>
      
      {/* Product Details with Padding */}
      <div style={{ padding: 16, background: '#fff', width: '100%' }}>
        {/* Product Name and Price Row */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 4,
          width: '100%' 
        }}>
          <div style={{ 
            fontWeight: 700, 
            fontSize: 16, 
            color: NEUTRAL_TEXT, 
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%'
          }}>
            {product.name}
          </div>
          <div style={{ 
            color: NEUTRAL_TEXT, 
            fontWeight: 700, 
            fontSize: 16,
            textAlign: 'right' 
          }}>
            ${Math.round(Number(product.price || 0))}
          </div>
        </div>
        
        {/* Product Rating Row */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-start', 
          alignItems: 'center', 
          marginBottom: 8,
          width: '100%' 
        }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenReviews(product);
            }}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 4,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0
            }}
          >
            <Star set="bold" primaryColor="#F59E0B" size={16} />
            <span style={{ 
              fontSize: 12, 
              color: NEUTRAL_TEXT, 
              fontWeight: 500 
            }}>
              {product.rating?.toFixed(1) || '4.5'}
            </span>
            {product.reviewCount > 0 && (
              <span style={{ 
                fontSize: 10, 
                color: ACCENT_GRAY, 
                fontWeight: 400 
              }}>
                ({product.reviewCount})
              </span>
            )}
          </button>
        </div>
        
        {/* Full Width Quick Add Button */}
        {getCartQty(product.id) === 0 ? (
          <button
            style={{
              width: '100%',
              background: addStatus[product.id] === 'added' ? '#10B981' : '#f4a261',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 12,
              fontWeight: 600,
              cursor: addStatus[product.id] === 'adding' ? 'wait' : 'pointer',
              transition: 'background-color 0.2s ease',
              opacity: addStatus[product.id] === 'adding' ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (addStatus[product.id] !== 'added') {
                e.target.style.background = '#e89b5a';
              }
            }}
            onMouseLeave={(e) => {
              if (addStatus[product.id] !== 'added') {
                e.target.style.background = addStatus[product.id] === 'added' ? '#10B981' : '#f4a261';
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              onAddToCart(product, e);
            }}
            disabled={addStatus[product.id] === 'adding'}
          >
                                    {addStatus[product.id] === 'added' ? (
                          <span style={{ fontSize: 12, lineHeight: 1 }}>✔</span>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%' }}>
                            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
                            <span>Quick add</span>
                          </div>
                        )}
          </button>
        ) : (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            background: '#f4a261', 
            borderRadius: 8, 
            padding: '8px 12px', 
            width: '100%', 
            justifyContent: 'center',
            minHeight: '32px'
          }}>
            {/* Decrement or Trash */}
            {getCartQty(product.id) > 1 ? (
              <button
                onClick={e => {
                  e.stopPropagation();
                  updateCartItem(product.id, getCartQty(product.id) - 1);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: 16,
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  borderRadius: 4
                }}
                aria-label="Decrease quantity"
              >-</button>
            ) : (
              <button
                onClick={e => {
                  e.stopPropagation();
                  removeCartItem(product.id);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  borderRadius: 4
                }}
                aria-label="Remove from cart"
              >
                {/* Trash can icon, white */}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18" />
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </button>
            )}
            {/* Quantity */}
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 12, minWidth: 16, textAlign: 'center' }}>{getCartQty(product.id)}</span>
            {/* Increment */}
            <button
              onClick={e => {
                e.stopPropagation();
                updateCartItem(product.id, getCartQty(product.id) + 1);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#fff',
                fontSize: 16,
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderRadius: 4
              }}
              aria-label="Increase quantity"
            >+</button>
          </div>
        )}
      </div>
    </div>
  );

  // If drag and drop is disabled, render a simple grid without @dnd-kit overhead
  if (!editMode) {
    console.log('[ProductFeedView] Rendering simple grid (no drag & drop)');
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'calc(50% - 10px) calc(50% - 10px)',
        gap: 20,
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto'
      }}>
        {feed.map((item, index) => (
          <div key={item.id} style={{
            width: '100%',
            height: 'fit-content',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {renderProductCard(item, index)}
          </div>
        ))}
      </div>
    );
  }

  console.log('[ProductFeedView] Rendering with drag & drop enabled');

  // Only use @dnd-kit when edit mode is enabled
  return (
    <SortableList
      items={feed}
      onReorder={handleReorder}
      renderItem={renderProductCard}
      direction="horizontal"
      disabled={false}
      dragHandle={true}
    />
  );
}

export function Storefront() {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { addCartItem, cartItems, updateCartItem, removeCartItem } = useCart();
  // Zustand stores
  const {
    shop: shopStore,
    products: productsStore,
    editMode,
    showDeleteConfirm,
    productToDelete,
    loading: shopLoading,
    errors: shopErrors,
    setShop,
    setProducts,
    setEditMode,
    setShowDeleteConfirm,
    setProductToDelete,
    setLoading: setShopLoading,
    setError: setShopError,
    clearError: clearShopError,
  } = useShopStore();

  const {
    setLoading: setAppLoading,
    setError: setAppError,
    clearError: clearAppError,
  } = useAppStore();

  // Local state for file handling and other UI state
  const [editShop, setEditShop] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [coverPhotoFile, setCoverPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Cart state for tracking add status
  const [addStatus, setAddStatus] = useState({});
  
  // Variant selection state
  const [showVariantSheet, setShowVariantSheet] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [variantSelections, setVariantSelections] = useState({});
  const [quantity, setQuantity] = useState(1);
  
  // Auto-play state for videos
  const [currentPlayingIndex, setCurrentPlayingIndex] = useState(0);
  const [videoRefs, setVideoRefs] = useState([]);
  
  // Store rating state
  const [storeRating, setStoreRating] = useState({ avgRating: 0, reviewCount: 0 });
  
  // Products with real ratings
  const [productsWithRatings, setProductsWithRatings] = useState([]);
  
  // Reviews drawer state
  const [showReviewsDrawer, setShowReviewsDrawer] = useState(false);
  const [selectedProductForReviews, setSelectedProductForReviews] = useState(null);
  
  // Helper to get current quantity in cart
  const getCartQty = (itemId) => {
    const found = cartItems.find(i => i.id === itemId);
    return found ? found.qty : 0;
  };
  
  // Legacy drag and drop state (kept for compatibility)
  const [isDragging, setIsDragging] = useState(false);
  const [draggedProductId, setDraggedProductId] = useState(null);
  const [dragOverProductId, setDragOverProductId] = useState(null);
  const [reorderError, setReorderError] = useState('');
  const [isReordering, setIsReordering] = useState(false);
  const [skipNextFetch, setSkipNextFetch] = useState(false);
  const profileInputRef = useRef();

  // Initialize display order for products that don't have it
  const initializeDisplayOrder = async (productsList) => {
    const productsNeedingOrder = productsList.filter(p => p.displayOrder === undefined);
    
    if (productsNeedingOrder.length === 0) {
      console.log('[Storefront] All products already have displayOrder');
      return;
    }
    
    console.log('[Storefront] Initializing displayOrder for products:', productsNeedingOrder.map(p => p.name));
    
    try {
      const batch = writeBatch(db);
      
      // Set display order based on current array position
      productsList.forEach((product, index) => {
        if (product.displayOrder === undefined) {
          const productRef = doc(db, 'products', product.id);
          batch.update(productRef, { displayOrder: index });
          console.log(`[Storefront] Initializing product ${product.id} (${product.name}) to displayOrder: ${index}`);
        }
      });
      
      await batch.commit();
      console.log('[Storefront] Display order initialized successfully');
      
      // Update local state to reflect the new displayOrder values
      const updatedProducts = productsList.map((product, index) => ({
        ...product,
        displayOrder: product.displayOrder !== undefined ? product.displayOrder : index
      }));
      
      setProducts(updatedProducts);
      
    } catch (err) {
      console.error('[Storefront] Error initializing display order:', err);
    }
  };

  // React Query hooks for data fetching
  const {
    data: shopData,
    isLoading: shopQueryLoading,
    error: shopError,
  } = useShopBySeller(sellerId);

  // Get the actual sellerId from shop data (in case sellerId param is a shop document ID)
  const actualSellerId = shopData?.sellerId || sellerId;

  // Use normal hook that works, and filter on client side for now
  // Wait for shopData to load first so we can get the correct sellerId
  const {
    data: productsData = [],
    isLoading: productsQueryLoading,
    error: productsError,
  } = useProductsBySeller(actualSellerId, {
    enabled: !!actualSellerId && (!shopQueryLoading || !!shopData)
  });

  // Reorder products mutation
  const reorderProductsMutation = useReorderProducts();

  // Set shop data in Zustand store when fetched
  React.useEffect(() => {
    if (shopData) {
      setShop(shopData);
      setEditShop(shopData);
    }
  }, [shopData, setShop]);

  // Set products data in Zustand store when fetched
  React.useEffect(() => {
    if (productsData.length > 0) {
      setProducts(productsData);
    }
  }, [productsData, setProducts]);

  // Initialize display order for products that don't have it
  React.useEffect(() => {
    if (productsData.length > 0) {
      initializeDisplayOrder(productsData);
    }
  }, [productsData]);

  // Fetch user's favorited shops
  React.useEffect(() => {
    if (currentUser?.uid && shopData) {
      const fetchFavorites = async () => {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const favShops = userSnap.data().favorited_shops || [];
            setIsFavorited(favShops.includes(sellerId));
          }
        } catch (error) {
          console.error('Failed to fetch favorites:', error);
        }
      };
      fetchFavorites();
    }
  }, [currentUser?.uid, shopData, sellerId]);

  // Calculate store rating from products' average ratings
  React.useEffect(() => {
    if (productsWithRatings.length > 0) {
      const calculateStoreRating = () => {
        try {
          // Filter out products with no ratings (rating === 0)
          const productsWithRatingsOnly = productsWithRatings.filter(p => p.rating > 0);
          
          if (productsWithRatingsOnly.length === 0) {
            setStoreRating({ avgRating: 0, reviewCount: 0 });
            return;
          }
          
          // Calculate average rating from all products
          const totalRating = productsWithRatingsOnly.reduce((sum, product) => sum + product.rating, 0);
          const avgRating = totalRating / productsWithRatingsOnly.length;
          
          // Calculate total review count from all products
          const totalReviewCount = productsWithRatingsOnly.reduce((sum, product) => sum + product.reviewCount, 0);
          
          setStoreRating({ 
            avgRating: avgRating, 
            reviewCount: totalReviewCount 
          });
          
          console.log('[Storefront] Store rating calculated from products:', {
            productsWithRatings: productsWithRatingsOnly.length,
            avgRating: avgRating.toFixed(2),
            totalReviewCount,
            productRatings: productsWithRatingsOnly.map(p => ({ 
              name: p.name, 
              rating: p.rating.toFixed(2), 
              reviewCount: p.reviewCount 
            }))
          });
        } catch (error) {
          console.error('Error calculating store rating:', error);
        }
      };
      calculateStoreRating();
    }
  }, [productsWithRatings]);

  // Load real ratings for each product
  React.useEffect(() => {
    if (productsData.length > 0) {
      const loadProductRatings = async () => {
        try {
          const productsWithRealRatings = await Promise.all(
            productsData.map(async (product) => {
              try {
                const reviews = await getProductReviews(product.id);
                const avgRating = reviews.length > 0 
                  ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length 
                  : 0;
                return {
                  ...product,
                  rating: avgRating,
                  reviewCount: reviews.length
                };
              } catch (error) {
                console.error(`Error loading reviews for product ${product.id}:`, error);
                return {
                  ...product,
                  rating: 0,
                  reviewCount: 0
                };
              }
            })
          );
          setProductsWithRatings(productsWithRealRatings);
        } catch (error) {
          console.error('Error loading product ratings:', error);
          setProductsWithRatings(productsData);
        }
      };
      loadProductRatings();
    }
  }, [productsData]);

  // React Query handles the data fetching automatically
  // No need for manual useEffect to fetch data

  // Log products for debugging if needed
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Storefront] Products updated:', {
        count: productsData.length,
        editMode
      });
    }
  }, [productsData, editMode]);

  // Customer view - no editing allowed
  const canEdit = false;

  // Track store view for analytics
  useEffect(() => {
    if (shopData?.id) {
      const sessionId = generateSessionId();
      trackStoreView(shopData.id, sessionId, currentUser?.uid);
    }
  }, [shopData?.id, currentUser?.uid]);

  // Initialize video refs when products change
  useEffect(() => {
    // No need to filter here since API handles it
    if (productsData.length > 0 && videoRefs.length !== productsData.length) {
      setVideoRefs(new Array(productsData.length).fill(null));
      setCurrentPlayingIndex(0);
    }
  }, [productsData, videoRefs.length]);

  // Video autoplay effect
  useEffect(() => {
    // No need to filter here since API handles it
    if (productsData.length === 0 || videoRefs.length === 0) {
      return;
    }

    const currentVideo = videoRefs[currentPlayingIndex];
    const currentProduct = productsData[currentPlayingIndex];
    
    // Skip products without video URLs and advance to the next one
    if (!currentVideo || !currentProduct || !currentProduct.videoUrl) {
      const nextIndex = (currentPlayingIndex + 1) % productsData.length;
      setCurrentPlayingIndex(nextIndex);
      return;
    }

    // Set up event listener for when video ends
    const handleVideoEnd = () => {
      const nextIndex = (currentPlayingIndex + 1) % productsData.length;
      setCurrentPlayingIndex(nextIndex);
    };

    const handleVideoError = (error) => {
      console.log('Video error:', error);
      // Move to next video on error
      const nextIndex = (currentPlayingIndex + 1) % productsData.length;
      setCurrentPlayingIndex(nextIndex);
    };

    // Add event listeners
    currentVideo.addEventListener('ended', handleVideoEnd);
    currentVideo.addEventListener('error', handleVideoError);

    // Try to play the current video with proper error handling
    const playVideo = async () => {
      try {
        // Check if video is already playing to prevent interruption
        if (currentVideo.readyState >= 2 && !currentVideo.paused) {
          return; // Video is already playing
        }
        
        // Reset video to beginning
        currentVideo.currentTime = 0;
        
        // Wait a bit to ensure video is ready
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Only play if not already playing
        if (currentVideo.paused) {
          await currentVideo.play();
        }
      } catch (error) {
        console.log('Autoplay prevented or failed for video', currentPlayingIndex, error);
        // Move to next video if autoplay fails
        const nextIndex = (currentPlayingIndex + 1) % productsData.length;
        setCurrentPlayingIndex(nextIndex);
      }
    };

    // Start playing after a short delay to ensure refs are stable
    const playTimer = setTimeout(playVideo, 200);

    return () => {
      clearTimeout(playTimer);
      currentVideo.removeEventListener('ended', handleVideoEnd);
      currentVideo.removeEventListener('error', handleVideoError);
    };
  }, [currentPlayingIndex, productsData, videoRefs]);



  // Determine if there are unsaved changes
  const hasUnsavedChanges = editMode && (
    (editShop?.description !== shopData?.description) ||
    (editShop?.profileImage !== shopData?.profileImage) ||
    isDragging || // Consider drag operations as unsaved changes
    draggedProductId !== null // Consider any active drag as unsaved
  );

  // Handlers for editing shop info
  const handleProfileImageChange = e => {
    const file = e.target.files[0];
    if (file) {
      setProfileImageFile(file);
      setEditShop(s => ({ ...s, profileImage: URL.createObjectURL(file) }));
    }
  };
  const handleCoverPhotoChange = e => {
    const file = e.target.files[0];
    if (file) {
      setCoverPhotoFile(file);
      setEditShop(s => ({ ...s, coverPhoto: URL.createObjectURL(file) }));
    }
  };
  const handleDescriptionChange = e => {
    setEditShop(s => ({ ...s, description: e.target.value }));
  };
  const handleSaveChanges = async () => {
    if (!editShop) return;
    setSaving(true);
    try {
      let profileImageUrl = editShop.profileImage;
      let coverPhotoUrl = editShop.coverPhoto;
      if (profileImageFile) {
        const uploadResult = await uploadProductImage(profileImageFile, sellerId, 'profile');
        profileImageUrl = uploadResult.url;
      }
      if (coverPhotoFile) {
        const uploadResult = await uploadShopCoverPhoto(coverPhotoFile, sellerId);
        coverPhotoUrl = uploadResult.url;
      }
      const shopRef = doc(db, 'shops', shopData.id);
      // Only update fields that are not empty strings
      const updateData = {};
      if (profileImageUrl) updateData.profileImage = profileImageUrl;
      if (coverPhotoUrl) updateData.coverPhotoURL = coverPhotoUrl;
      if (editShop.description !== undefined) updateData.description = editShop.description;
      await updateDoc(shopRef, updateData);
      setShop(s => ({ ...s, ...updateData }));
      setProfileImageFile(null);
      setCoverPhotoFile(null);
      
      // Save product order changes if any
          if (productsData.length > 0) {
      const productOrder = productsData.map(p => p.id);
        await saveProductOrder(productOrder);
      }
      
      setSaving(false);
              shopToasts.saved();
    } catch (err) {
      setSaving(false);
      console.error('Failed to save changes:', err);
              shopToasts.error('save');
    }
  };

  // Product management handlers
  const handleAddProduct = () => navigate('/add-product');
  const handleEditProduct = productId => navigate(`/edit-product/${productId}`);
  const handleDeleteProduct = async productId => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      const productRef = doc(db, 'products', productId);
      await productRef.delete();
              setProducts(productsData => productsData.filter(p => p.id !== productId));
    } catch (err) {
              productToasts.error('delete');
    }
  };

  const handleFavorite = async () => {
    if (!currentUser?.uid || !shopData) return;
    const userRef = doc(db, 'users', currentUser.uid);
    setIsFavorited(fav => !fav); // Optimistic UI
    try {
      if (!isFavorited) {
        await updateDoc(userRef, { favorited_shops: arrayUnion(sellerId) });
      } else {
        await updateDoc(userRef, { favorited_shops: arrayRemove(sellerId) });
      }
    } catch (err) {
      // Revert UI on error
      setIsFavorited(fav => !fav);
              showError('Failed to update favorites');
    }
  };

  const handleAddToCart = async (product, event) => {
    event.stopPropagation(); // Prevent navigation when clicking add to cart
    
    // Check if product has variants
    const hasVariants = (product.variants && product.variants.length > 0) ||
                       (product.variations && product.variations.length > 0) ||
                       (product.sizes && product.sizes.length > 0) ||
                       (product.spiceLevels && product.spiceLevels.length > 0);
    
    if (hasVariants) {
      console.log('[Storefront] Product has variants, opening variant selection sheet');
      // Open variant selection sheet instead of adding directly
      setSelectedProduct(product);
      setVariantSelections({});
      setQuantity(1);
      setShowVariantSheet(true);
      return;
    } else {
      console.log('[Storefront] Product has no variants, proceeding with direct add to cart');
    }
    
    // Set adding status
    setAddStatus(prev => ({ ...prev, [product.id]: 'adding' }));
    
    try {
      // Calculate next available fulfillment day for this product
      let fulfillmentDay = null;
      if (product.inventory?.scheduled && Array.isArray(product.inventory.scheduled)) {
        const today = new Date();
        const todayString = today.toISOString().split('T')[0];
        const policy = product.policy;
        
        const nextAvailableDay = product.inventory.scheduled
          .filter(row => {
            // Check if date has available units
            if (row.available_total <= 0) return false;
            
            // Check if date is in the future
            if (row.service_date <= todayString) return false;
            
            // Check policy restrictions
            if (policy) {
              const orderDate = new Date(row.service_date);
              const daysDiff = Math.ceil((orderDate - today) / (1000 * 60 * 60 * 24));
              
              // Check min_order_ahead policy
              if (policy.min_order_ahead && policy.min_order_ahead.value) {
                const minDays = policy.min_order_ahead.value;
                if (daysDiff < minDays) return false;
              }
              
              // Check same_day_allowed policy
              if (!policy.same_day_allowed && daysDiff === 0) return false;
            }
            
            return true;
          })
          .sort((a, b) => new Date(a.service_date) - new Date(b.service_date))
          .find(row => new Date(row.service_date) > today);
        
        if (nextAvailableDay) {
          fulfillmentDay = nextAvailableDay.service_date;
        }
      }
      
      await addCartItem({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.imageUrl || product.image,
        qty: 1,
        fulfillmentDay: fulfillmentDay, // Add the calculated fulfillment day
        sellerId: shopData?.id, // Include sellerId for discount validation
        category: product.category // Include category for discount scope validation
      });
      console.log('Added to cart:', product.name);
      productToasts.added(product.name);
      
      // Set added status
      setAddStatus(prev => ({ ...prev, [product.id]: 'added' }));
      
      // Reset status after a delay
      setTimeout(() => {
        setAddStatus(prev => ({ ...prev, [product.id]: undefined }));
      }, 2000);
    } catch (err) {
      console.error('Error adding to cart:', err);
      showError('Failed to add to cart');
      
      // Reset status on error
      setAddStatus(prev => ({ ...prev, [product.id]: undefined }));
    }
  };

  // Variant selection functions
  const handleVariantSelection = (variantKey, optionValue) => {
    setVariantSelections(prev => ({
      ...prev,
      [variantKey]: optionValue
    }));
  };

  const allVariantsSelected = () => {
    if (!selectedProduct) return false;
    
    const variants = selectedProduct.variants || selectedProduct.variations || selectedProduct.sizes || selectedProduct.spiceLevels || [];
    return variants.every(variant => variantSelections[variant.key || variant.name] !== undefined);
  };

  const handleAddToCartWithVariants = async () => {
    if (!selectedProduct || !allVariantsSelected()) return;

    try {
      setAddStatus(prev => ({ ...prev, [selectedProduct.id]: 'adding' }));
      
      // Calculate next available fulfillment day for this product
      let fulfillmentDay = null;
      if (selectedProduct.inventory?.scheduled && Array.isArray(selectedProduct.inventory.scheduled)) {
        const today = new Date();
        const todayString = today.toISOString().split('T')[0];
        const policy = selectedProduct.policy;
        
        const nextAvailableDay = selectedProduct.inventory.scheduled
          .filter(row => {
            if (row.available_total <= 0) return false;
            if (row.service_date <= todayString) return false;
            
            if (policy) {
              const orderDate = new Date(row.service_date);
              const daysDiff = Math.ceil((orderDate - today) / (1000 * 60 * 60 * 24));
              
              if (policy.min_order_ahead && policy.min_order_ahead.value) {
                const minDays = policy.min_order_ahead.value;
                if (daysDiff < minDays) return false;
              }
              
              if (!policy.same_day_allowed && daysDiff === 0) return false;
            }
            
            return true;
          })
          .sort((a, b) => new Date(a.service_date) - new Date(b.service_date))
          .find(row => new Date(row.service_date) > today);
        
        if (nextAvailableDay) {
          fulfillmentDay = nextAvailableDay.service_date;
        }
      }

      // Create unique ID that includes variant information
      const variantString = Object.keys(variantSelections).length > 0 
        ? Object.entries(variantSelections)
            .map(([key, value]) => `${key}:${value}`)
            .sort()
            .join('|')
        : '';
      const uniqueId = variantString ? `${selectedProduct.id}_${variantString}` : selectedProduct.id;

      await addCartItem({
        id: uniqueId,
        productId: selectedProduct.id,
        name: selectedProduct.name,
        image: selectedProduct.imageUrl || selectedProduct.image,
        price: selectedProduct.price,
        qty: quantity,
        fulfillmentDay: fulfillmentDay,
        sellerId: shopData?.id,
        category: selectedProduct.category,
        variant: Object.keys(variantSelections).length > 0 ? variantSelections : null
      });

      console.log('Added to cart with variants:', selectedProduct.name);
      productToasts.added(selectedProduct.name);
      
      setAddStatus(prev => ({ ...prev, [selectedProduct.id]: 'added' }));
      setTimeout(() => setAddStatus(prev => ({ ...prev, [selectedProduct.id]: undefined })), 2000);
      
      // Close the sheet
      setShowVariantSheet(false);
      setSelectedProduct(null);
      setVariantSelections({});
      setQuantity(1);
      
    } catch (err) {
      console.error('Error adding to cart with variants:', err);
      showError('Failed to add to cart');
      setAddStatus(prev => ({ ...prev, [selectedProduct.id]: undefined }));
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleOpenReviews = (product) => {
    setSelectedProductForReviews(product);
    setShowReviewsDrawer(true);
  };

  const handleCloseReviews = () => {
    setShowReviewsDrawer(false);
    setSelectedProductForReviews(null);
  };

  // @dnd-kit product reordering handler with React Query
  const handleProductReorder = async (newOrder) => {
    console.log('[Storefront] Products reordered with @dnd-kit:', newOrder);
    
    if (!editMode || !shopData?.id) return;
    
    // Update local state immediately for visual feedback
    console.log('[Storefront] Setting new products state...');
    setProducts(newOrder);
    
    // Use React Query mutation for backend update
    try {
      console.log('[Storefront] Saving to backend...');
      await reorderProductsMutation.mutateAsync({
        sellerId: shopData.id,
        newOrder,
      });
      console.log('[Storefront] Product order saved successfully');
      productToasts.reordered();
    } catch (error) {
      console.error('[Storefront] Failed to save product order:', error);
      // React Query will automatically rollback on error
    }
  };

  // Legacy drag and drop handlers (kept for compatibility)
  const handleDragStart = (e, productId) => {
    console.log('[Storefront] Drag start called for product:', productId);
    console.log('[Storefront] Edit mode:', editMode);
    console.log('[Storefront] Target element:', e.target);
    console.log('[Storefront] Drag handle check:', e.target.closest('.drag-handle'));
    
    if (!editMode) {
      console.log('[Storefront] Drag blocked - not in edit mode');
      return;
    }
    
    // Only allow drag if clicking the drag handle
    if (!e.target.closest('.drag-handle')) {
      console.log('[Storefront] Drag blocked - not on drag handle');
      e.preventDefault();
      return;
    }
    
    console.log('[Storefront] Drag allowed - starting drag for product:', productId);
    setIsDragging(true);
    setDraggedProductId(productId);
    e.dataTransfer.effectAllowed = 'move';
    
    // Set clean product ID in data transfer - use multiple formats for compatibility
    e.dataTransfer.setData('text/plain', productId);
    e.dataTransfer.setData('text', productId);
    
    console.log('[Storefront] Data transfer set with product ID:', productId);
  };

  const handleDragOver = (e, productId) => {
    if (!editMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (productId !== draggedProductId) {
      setDragOverProductId(productId);
    }
  };

  const handleDragLeave = (e) => {
    if (!editMode) return;
    setDragOverProductId(null);
  };

  const handleDrop = async (e, targetProductId) => {
    console.log('[Storefront] Drop called for target product:', targetProductId);
    console.log('[Storefront] Edit mode:', editMode);
    console.log('[Storefront] Current products before reorder:', productsData.map(p => ({ id: p.id, name: p.name })));
    
    if (!editMode) return;
    e.preventDefault();
    
    const draggedId = e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('text');
    console.log('[Storefront] Dragged ID from data transfer:', draggedId);
    
    if (!draggedId) {
      console.log('[Storefront] No dragged ID found in data transfer');
      setDragOverProductId(null);
      setIsDragging(false);
      setDraggedProductId(null);
      return;
    }

    if (draggedId === targetProductId) {
      console.log('[Storefront] Same product dropped on itself, no reordering needed');
      setDragOverProductId(null);
      return;
    }

    const draggedIndex = productsData.findIndex(p => p.id === draggedId);
    const targetIndex = productsData.findIndex(p => p.id === targetProductId);
    
    console.log('[Storefront] Product ID matching debug:', {
      draggedId,
      targetProductId,
      productsIds: productsData.map(p => p.id),
      draggedIndex,
      targetIndex,
      products: productsData.map(p => ({ id: p.id, name: p.name, type: typeof p.id }))
    });
    
    if (draggedIndex === -1 || targetIndex === -1) {
      console.log('[Storefront] Invalid indices:', { draggedIndex, targetIndex });
      console.log('[Storefront] Available product IDs:', productsData.map(p => p.id));
      console.log('[Storefront] Dragged ID not found in products array');
      setDragOverProductId(null);
      setIsDragging(false);
      setDraggedProductId(null);
      return;
    }

    console.log('[Storefront] Reordering products:', { 
      draggedIndex, 
      targetIndex, 
      draggedId, 
      targetProductId,
      draggedProductName: productsData[draggedIndex]?.name,
      targetProductName: productsData[targetIndex]?.name
    });

    // Create new products array with reordered items
    const newProducts = [...productsData];
    const [draggedProduct] = newProducts.splice(draggedIndex, 1);
    newProducts.splice(targetIndex, 0, draggedProduct);
    
    console.log('[Storefront] New products order:', newProducts.map(p => ({ id: p.id, name: p.name })));
    
    // Set flag to prevent next fetch from overriding our reordering
    setSkipNextFetch(true);
    
    // Update local state immediately for visual feedback
    console.log('[Storefront] Setting new products state...');
    setProducts(newProducts);
    
    // Reset drag states
    setDragOverProductId(null);
    setIsDragging(false);
    setDraggedProductId(null);
    
    // Save the new order to backend
    try {
      console.log('[Storefront] Saving to backend...');
      await saveProductOrder(newProducts);
      console.log('[Storefront] Product order saved successfully');
    } catch (error) {
      console.error('[Storefront] Failed to save product order:', error);
      // Optionally revert the local state on error
      // setProducts(products);
    }
  };

  const saveProductOrder = async (productOrder) => {
    try {
      console.log('[Storefront] Saving product order:', productOrder.map(p => ({ id: p.id, name: p.name })));
      
      const batch = writeBatch(db);
      
      // Update each product with its new display order
      productOrder.forEach((product, index) => {
        const productRef = doc(db, 'products', product.id);
        batch.update(productRef, { displayOrder: index });
        console.log(`[Storefront] Setting product ${product.id} (${product.name}) to displayOrder: ${index}`);
      });
      
      await batch.commit();
      console.log('[Storefront] Product order saved to database successfully');
      
    } catch (err) {
      console.error('[Storefront] Error saving product order:', err);
      throw new Error('Failed to save product order');
    }
  };

  const handleProductClick = (productId) => {
    console.log('[Storefront] Product clicked:', productId);
    console.log('[Storefront] Edit mode:', editMode);
    console.log('[Storefront] Shop ID:', shopData?.id);
    console.log('[Storefront] Shop object:', shopData);
    console.log('[Storefront] sellerId from params:', sellerId);
    
    // Allow navigation even in edit mode since drag and drop is isolated to drag handle
    // if (editMode) {
    //   console.log('[Storefront] Navigation blocked - edit mode active');
    //   return; // Don't navigate when in edit mode
    // }
    
    if (!shopData?.id) {
      console.log('[Storefront] Navigation blocked - no shop ID');
      return;
    }
    
    // Create product order string to preserve the order from storefront
    const productOrder = productsData.map(p => p.id).join(',');
    
    // Navigate to products page with store context and product order
    const url = `/products/${productId}?storeId=${sellerId}&order=${productOrder}`;
    
    console.log('[Storefront] Navigating to:', url);
    navigate(url);
  };

  const handleVideoPlay = (video) => {
    if (editMode) return; // Don't play videos when in edit mode
    
    try {
      if (video.paused) {
        video.play();
      }
    } catch (error) {
      console.log('Video play error:', error);
    }
  };

  const handleVideoPause = (video) => {
    if (editMode) return; // Don't pause videos when in edit mode
    
    try {
      if (!video.paused) {
        video.pause();
      }
    } catch (error) {
      console.log('Video pause error:', error);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: NEUTRAL_WHITE, paddingBottom: '80px' }}>
      {/* Header */}
      <header style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 100,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          padding: '16px 20px',
          gap: 16,
          position: 'relative'
        }}>
          <BackButton
            onClick={() => navigate(-1)}
            variant="standard"
            ariaLabel="Back"
          />
          <div style={{ flex: 1 }} />
          {/* Save icon in header, only in edit mode and if there are unsaved changes */}
          {editMode && hasUnsavedChanges && (
            <button
              onClick={handleSaveChanges}
              disabled={saving}
              aria-label="Save changes"
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                marginLeft: 12,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <TickSquare set="bold" primaryColor={PRIMARY_BROWN} size={28} />
            </button>
          )}
        </div>
      </header>

      {/* Shop Info Section (profile image left, info right) */}
      <section style={{
        marginTop: 72,
        background: NEUTRAL_WHITE,
        borderRadius: 20,
        boxShadow: '0 2px 8px #0001',
        maxWidth: 480,
        marginLeft: 'auto',
        marginRight: 'auto',
        padding: 32,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 24
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          width: '100%',
          gap: 24,
          justifyContent: 'center',
          marginBottom: 16
        }}>
          {/* Logo as round circle */}
          <div style={{
            width: 88,
            height: 88,
            borderRadius: '50%',
            background: '#E5C7B2',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            position: 'relative'
          }}>
            <img
              src={editMode 
                ? (editShop?.coverPhotoURL || editShop?.coverphotoURL || editShop?.coverPhoto || editShop?.profileImage || undefined)
                : (shopData?.coverPhotoURL || shopData?.coverphotoURL || shopData?.coverPhoto || shopData?.profileImage || undefined)
              }
              alt={`${shopData?.name} logo`}
              width={88}
              height={88}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', cursor: editMode ? 'pointer' : 'default' }}
              onClick={editMode ? () => profileInputRef.current && profileInputRef.current.click() : undefined}
            />
            {editMode && (
              <input
                ref={profileInputRef}
                id="profile-image-input"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleProfileImageChange}
              />
            )}
            {editMode && (
              <label style={{ position: 'absolute', bottom: 8, right: 8, padding: 0, cursor: 'pointer', background: 'transparent', borderRadius: 0, boxShadow: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }} htmlFor="profile-image-input">
                <Edit set="light" primaryColor="#fff" size={24} />
              </label>
            )}
          </div>
          {/* Shop Name and Description */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', flex: 1 }}>
            <h1 style={{ ...headingFont, fontSize: 16, color: NEUTRAL_TEXT, margin: 0, textAlign: 'left' }}>{shopData?.name}</h1>
            {editMode ? (
              <textarea
                value={editShop?.description || ''}
                onChange={handleDescriptionChange}
                style={{ color: ACCENT_GRAY, fontSize: 12, marginTop: 6, textAlign: 'left', ...bodyFont, width: '100%', minHeight: 40, borderRadius: 8, border: '1px solid #eee', padding: 8 }}
              />
            ) : (
              <div style={{ color: ACCENT_GRAY, fontSize: 12, marginTop: 6, textAlign: 'left', ...bodyFont }}>{shopData?.description}</div>
            )}
          </div>
        </div>
        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 16, marginTop: 8, width: '100%', justifyContent: 'center' }}>
          <button
            onClick={handleFavorite}
            aria-label={isFavorited ? 'Unfavorite shop' : 'Favorite shop'}
            style={{
              background: isFavorited 
                ? 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)'
                : 'transparent',
              backgroundSize: isFavorited ? '200% 200%' : 'auto',
              animation: isFavorited ? 'gradientShift 3s ease infinite' : 'none',
              color: isFavorited ? '#fff' : PRIMARY_BROWN,
              border: isFavorited ? 'none' : `2px solid ${PRIMARY_BROWN}`,
              borderRadius: 12,
              padding: '10px 32px',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              ...bodyFont,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 1px 4px #0001',
              transition: 'all 0.2s ease',
              onMouseEnter: (e) => {
                if (isFavorited) {
                  e.currentTarget.style.animation = 'gradientShift 1s ease infinite';
                }
              },
              onMouseLeave: (e) => {
                if (isFavorited) {
                  e.currentTarget.style.animation = 'gradientShift 3s ease infinite';
                }
              }
            }}
          >
            <Heart set={isFavorited ? 'bold' : 'light'} primaryColor={isFavorited ? '#fff' : PRIMARY_BROWN} size={20} />
            {isFavorited ? 'Unfavorite' : 'Favorite'}
          </button>
        </div>
      </section>

      {/* Metric Cards */}
      <section style={{
        maxWidth: 480,
        margin: '32px auto',
        background: NEUTRAL_WHITE,
        borderRadius: 20,
        boxShadow: '0 2px 8px #0001',
        padding: 24
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 16
        }}>
          {/* Favorites Card */}
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: '20px 16px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8
          }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              lineHeight: 1
            }}>
                              {shopData?.favoritesCount || 0}
            </div>
            <div style={{
              fontSize: '12px',
              color: ACCENT_GRAY,
              fontWeight: '500'
            }}>
              Favorites
            </div>
          </div>

          {/* Rating Card */}
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: '20px 16px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8
          }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              lineHeight: 1
            }}>
              {storeRating.avgRating > 0 ? (
                <>
                  {storeRating.avgRating.toFixed(1)}
                  <span style={{
                    fontSize: '18px',
                    color: ACCENT_GRAY,
                    fontWeight: '400'
                  }}>/5</span>
                </>
              ) : (
                '--'
              )}
            </div>
            <div style={{
              fontSize: '12px',
              color: ACCENT_GRAY,
              fontWeight: '500'
            }}>
              Stars
            </div>
          </div>

          {/* Orders Card */}
          <div style={{
            background: NEUTRAL_WHITE,
            borderRadius: 12,
            padding: '20px 16px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8
          }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              lineHeight: 1
            }}>
                              {shopData?.completedOrdersCount || 0}
            </div>
            <div style={{
              fontSize: '12px',
              color: ACCENT_GRAY,
              fontWeight: '500'
            }}>
              Orders
            </div>
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section style={{
        maxWidth: 480,
        margin: '32px auto',
        background: NEUTRAL_WHITE,
        borderRadius: 20,
        boxShadow: '0 2px 8px #0001',
        padding: 24
      }}>

        {shopQueryLoading || productsQueryLoading ? (
          <div style={{ color: ACCENT_GRAY, fontSize: 18, textAlign: 'center', ...bodyFont }}>
            {shopQueryLoading ? 'Loading shop...' : 'Loading products...'}
          </div>
        ) : shopError ? (
          <div style={{ color: ERROR_RED, fontSize: 18, textAlign: 'center', ...bodyFont }}>{shopError.message}</div>
        ) : productsError ? (
          <div style={{ color: ERROR_RED, fontSize: 18, textAlign: 'center', ...bodyFont }}>{productsError.message}</div>
        ) : (
          USE_VIDEO_FEED
            ? (
              <>
                {/* Reorder Error Message */}
                {reorderError && (
                  <div style={{
                    background: '#FEE2E2',
                    border: '1px solid #FCA5A5',
                    borderRadius: 8,
                    padding: '12px 16px',
                    marginBottom: 16,
                    color: '#DC2626',
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                    {reorderError}
                  </div>
                )}
                
                {/* Reordering Indicator */}
                {isReordering && (
                  <div style={{
                    background: '#DBEAFE',
                    border: '1px solid #93C5FD',
                    borderRadius: 8,
                    padding: '12px 16px',
                    marginBottom: 16,
                    color: '#1E40AF',
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                    </svg>
                    Saving new product order...
                  </div>
                )}
                
                <ProductFeedView
                  products={productsWithRatings.length > 0 ? productsWithRatings : productsData}
                  editMode={editMode}
                  onReorder={handleProductReorder}
                  onProductClick={handleProductClick}
                  onVideoPlay={handleVideoPlay}
                  onVideoPause={handleVideoPause}
                  sellerId={shopData?.id}
                  onAddToCart={handleAddToCart}
                  getCartQty={getCartQty}
                  updateCartItem={updateCartItem}
                  removeCartItem={removeCartItem}
                  addStatus={addStatus}
                  videoRefs={videoRefs}
                  currentPlayingIndex={currentPlayingIndex}
                  onOpenReviews={handleOpenReviews}
                />
              </>
            )
            : (() => {
                // Filter products based on edit mode and availability
                const productsToUse = productsWithRatings.length > 0 ? productsWithRatings : productsData;
                const filteredProducts = editMode
                  ? productsToUse.filter(p => p.status !== false) // Show all active products for management
                  : productsToUse.filter(p => {
                      // For customer view, check if product is active AND has available stock
                      const isActive = p.published === true || p.status === true;
                      
                      // Check if product has unlimited stock
                      if (p.unlimited === true) return isActive;
                      
                      // Check regular stock field
                      if (p.stock > 0) return isActive;
                      
                      // Check new inventory schema for any available dates
                      if (p.inventory && p.inventory.scheduled && Array.isArray(p.inventory.scheduled)) {
                        const hasAvailableStock = p.inventory.scheduled.some(row => 
                          row.enabled && row.available_total > 0
                        );
                        if (hasAvailableStock) return isActive;
                      }
                      
                      // Check legacy daily inventory for any available dates
                      if (p.dailyInventory && typeof p.dailyInventory === 'object') {
                        const hasAvailableStock = Object.values(p.dailyInventory).some(stock => {
                          const numStock = Number(stock);
                          return !isNaN(numStock) && numStock > 0;
                        });
                        if (hasAvailableStock) return isActive;
                      }
                      
                      // Check daily stock field (legacy)
                      if (p.dailyStock && typeof p.dailyStock === 'object') {
                        const hasAvailableStock = Object.values(p.dailyStock).some(stock => {
                          const numStock = Number(stock);
                          return !isNaN(numStock) && numStock > 0;
                        });
                        if (hasAvailableStock) return isActive;
                      }
                      
                      return false; // No available stock found
                    });
                

                
                console.log('[Storefront] Grid view rendering:', {
                  totalProducts: productsToUse.length,
                  filteredProducts: filteredProducts.length,
                  editMode,
                  products: filteredProducts.map(p => ({ 
                    id: p.id, 
                    name: p.name, 
                    available: p.available, 
                    status: p.status,
                    hasImage: !!(p.imageUrl || p.coverPhoto || p.profileImage),
                    rating: p.rating,
                    reviewCount: p.reviewCount
                  })),
                  allProducts: productsToUse.map(p => ({ 
                    id: p.id, 
                    name: p.name, 
                    available: p.available, 
                    status: p.status,
                    rating: p.rating,
                    reviewCount: p.reviewCount
                  }))
                });
                


                // If no products after filtering, show a message
                if (filteredProducts.length === 0) {
                  return (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px 20px',
                      color: ACCENT_GRAY,
                      fontSize: 16,
                      ...bodyFont
                    }}>
                      {productsToUse.length === 0 
                        ? 'No products available yet.' 
                        : 'All products are currently unavailable.'
                      }
                    </div>
                  );
                }

                return (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'calc(50% - 10px) calc(50% - 10px)',
                    gap: 20,
                    width: '100%',
                    maxWidth: '100%',
                    margin: '0 auto'
                  }}>
                    {filteredProducts.map(product => (
                  <div
                    key={product.id}
                    style={{
                      background: '#fff',
                      borderRadius: 0,
                      boxShadow: '0 1px 4px #0001',
                      padding: 0,
                      cursor: 'pointer',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      transition: 'box-shadow 0.2s',
                      minHeight: 320,
                      width: '100%',
                      height: '100%',
                      boxSizing: 'border-box'
                    }}
                    onClick={() => handleProductClick(product.id)}
                    tabIndex={0}
                    aria-label={`View details for ${product.name}`}
                  >
                    {/* Product Image/Video - Full Width */}
                    <div style={{ 
                      position: 'relative', 
                      width: '100%',
                      aspectRatio: '9/16',
                      background: '#000'
                    }}>
                      {/* Video Player */}
                      <video
                        ref={(el) => {
                          if (el) {
                            const productIndex = productsToUse.findIndex(p => p.id === product.id);
                            if (productIndex !== -1) {
                              // Direct assignment to prevent infinite loops
                              videoRefs[productIndex] = el;
                            }
                          }
                        }}
                        src={product.videoUrl || product.imageUrl || '/default-product.png'}
                        poster={product.imageUrl || '/default-product.png'}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover',
                          backgroundColor: '#000',
                          opacity: currentPlayingIndex === productsToUse.findIndex(p => p.id === product.id) ? '1' : '0.7'
                        }}
                        muted
                        playsInline
                        preload="metadata"
                        onLoadedData={(e) => {
                          // Set opacity when video is loaded
                          if (product.videoUrl) {
                            e.target.style.opacity = '1';
                          }
                        }}
                      />
                      
                      {/* Play Button Overlay - Show for non-video items or when video is not playing */}
                      {(!product.videoUrl || currentPlayingIndex !== productsToUse.findIndex(p => p.id === product.id)) && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          background: 'rgba(0,0,0,0.6)',
                          borderRadius: '50%',
                          width: 30,
                          height: 30,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'none'
                        }}>
                          <div style={{
                            width: 0,
                            height: 0,
                            borderLeft: '8px solid white',
                            borderTop: '5px solid transparent',
                            borderBottom: '5px solid transparent',
                            marginLeft: 2
                          }} />
                        </div>
                      )}
                      
                      {/* Availability Chip */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '20px',
                          right: '20px',
                          zIndex: 1000,
                          background: 'rgba(255, 255, 255, 0.7)',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          fontSize: '8px',
                          fontWeight: 500,
                          color: '#333',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                          backdropFilter: 'blur(4px)',
                          border: 'none',
                          pointerEvents: 'none',
                        }}
                      >
                        {(() => {
                          // Calculate next available day based on product inventory
                          const today = new Date();
                          const todayString = today.toISOString().split('T')[0];
                          
                          // Use inventory data from the product object
                          if (product.inventory) {
                            const inventory = product.inventory;
                            
                            // Check if same-day orders are allowed
                            const sameDayAllowed = inventory.policy?.same_day_allowed || false;
                            
                            // Check if today has available inventory
                            const todayInventory = inventory.scheduled?.find(row => 
                              row.service_date === todayString && row.enabled && row.available_total > 0
                            );
                            
                            // Also check for dates within 1 day of today (timezone handling)
                            const todayPlusOne = new Date(today);
                            todayPlusOne.setDate(today.getDate() + 1);
                            const tomorrowString = todayPlusOne.toISOString().split('T')[0];
                            
                            const todayPlusOneInventory = inventory.scheduled?.find(row => 
                              row.service_date === tomorrowString && row.enabled && row.available_total > 0
                            );
                            
                            if (sameDayAllowed && (todayInventory || todayPlusOneInventory)) {
                              // Show "Available today" if same-day orders are allowed and inventory is available
                              return 'Available today';
                            }
                            
                            // Find the next available day with inventory
                            if (inventory.scheduled && Array.isArray(inventory.scheduled)) {
                              const todayString = today.toISOString().split('T')[0];
                              const policy = product.policy;
                              
                              const nextAvailableDay = inventory.scheduled
                                .filter(row => {
                                  // Check if date has available units
                                  if (row.available_total <= 0) return false;
                                  
                                  // Check if date is in the future
                                  if (row.service_date <= todayString) return false;
                                  
                                  // Check policy restrictions
                                  if (policy) {
                                    const orderDate = new Date(row.service_date);
                                    const daysDiff = Math.ceil((orderDate - today) / (1000 * 60 * 60 * 24));
                                    
                                    // Check min_order_ahead policy
                                    if (policy.min_order_ahead && policy.min_order_ahead.value) {
                                      const minDays = policy.min_order_ahead.value;
                                      if (daysDiff < minDays) return false;
                                    }
                                    
                                    // Check same_day_allowed policy
                                    if (!policy.same_day_allowed && daysDiff === 0) return false;
                                  }
                                  
                                  return true;
                                })
                                .sort((a, b) => new Date(a.service_date) - new Date(b.service_date))
                                .find(row => new Date(row.service_date) > today);
                              
                              if (nextAvailableDay) {
                                const nextDate = new Date(nextAvailableDay.service_date);
                                const options = { weekday: 'short', month: 'short', day: 'numeric' };
                                const formattedDate = nextDate.toLocaleDateString('en-US', options);
                                return `Available: ${formattedDate}`;
                              }
                            }
                          }
                          
                          // Fallback: show tomorrow's date
                          const tomorrow = new Date(today);
                          tomorrow.setDate(today.getDate() + 1);
                          const options = { weekday: 'short', month: 'short', day: 'numeric' };
                          const nextAvailableDay = tomorrow.toLocaleDateString('en-US', options);
                          
                          return `Available: ${nextAvailableDay}`;
                        })()}
                      </div>
                    </div>
                    
                    {/* Product Details with Padding */}
                    <div style={{ padding: 16, width: '100%' }}>
                      {/* Product Name and Price Row */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        marginBottom: 4,
                        width: '100%' 
                      }}>
                        <div style={{ 
                          fontWeight: 700, 
                          fontSize: 16, 
                          color: NEUTRAL_TEXT, 
                          textAlign: 'left',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}>
                          {product.name}
                        </div>
                        <div style={{ 
                          color: NEUTRAL_TEXT, 
                          fontWeight: 700, 
                          fontSize: 16,
                          textAlign: 'right' 
                        }}>
                          ${Math.round(Number(product.price || 0))}
                        </div>
                      </div>
                      
                      {/* Product Rating Row */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start', 
                        alignItems: 'center', 
                        marginBottom: 8,
                        width: '100%' 
                      }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenReviews(product);
                          }}
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 4,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0
                          }}
                        >
                          <Star set="bold" primaryColor="#F59E0B" size={16} />
                          <span style={{ 
                            fontSize: 12, 
                            color: NEUTRAL_TEXT, 
                            fontWeight: 500 
                          }}>
                            {product.rating?.toFixed(1) || '4.5'}
                          </span>
                          {product.reviewCount > 0 && (
                            <span style={{ 
                              fontSize: 10, 
                              color: ACCENT_GRAY, 
                              fontWeight: 400 
                            }}>
                              ({product.reviewCount})
                            </span>
                          )}
                        </button>
                      </div>
                      
                      {/* Full Width Quick Add Button */}
                      {getCartQty(product.id) === 0 ? (
                      <button
                        style={{
                          width: '100%',
                          background: addStatus[product.id] === 'added' ? '#10B981' : '#f4a261',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 8,
                          padding: '8px 12px',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: addStatus[product.id] === 'adding' ? 'wait' : 'pointer',
                          transition: 'background-color 0.2s ease',
                          opacity: addStatus[product.id] === 'adding' ? 0.7 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (addStatus[product.id] !== 'added') {
                            e.target.style.background = '#e89b5a';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (addStatus[product.id] !== 'added') {
                            e.target.style.background = addStatus[product.id] === 'added' ? '#10B981' : '#f4a261';
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToCart(product, e);
                        }}
                        disabled={addStatus[product.id] === 'adding'}
                      >
                        {addStatus[product.id] === 'added' ? (
                          <span style={{ fontSize: 12, lineHeight: 1 }}>✔</span>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%' }}>
                            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
                            <span>Quick add</span>
                          </div>
                        )}
                      </button>
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        background: '#f4a261', 
                        borderRadius: 8, 
                        padding: '8px 12px', 
                        width: '100%', 
                        justifyContent: 'center',
                        minHeight: '32px'
                      }}>
                        {/* Decrement or Trash */}
                        {getCartQty(product.id) > 1 ? (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              updateCartItem(product.id, getCartQty(product.id) - 1);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#fff',
                              fontSize: 16,
                              width: 20,
                              height: 20,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              borderRadius: 4
                            }}
                            aria-label="Decrease quantity"
                          >-</button>
                        ) : (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              removeCartItem(product.id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#fff',
                              width: 20,
                              height: 20,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              borderRadius: 4
                            }}
                            aria-label="Remove from cart"
                          >
                            {/* Trash can icon, white */}
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18" />
                              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              <line x1="10" y1="11" x2="10" y2="17" />
                              <line x1="14" y1="11" x2="14" y2="17" />
                            </svg>
                          </button>
                        )}
                        {/* Quantity */}
                        <span style={{ color: '#fff', fontWeight: 600, fontSize: 12, minWidth: 16, textAlign: 'center' }}>{getCartQty(product.id)}</span>
                        {/* Increment */}
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            updateCartItem(product.id, getCartQty(product.id) + 1);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#fff',
                            fontSize: 16,
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            borderRadius: 4
                          }}
                          aria-label="Increase quantity"
                        >+</button>
                      </div>
                    )}
                    
                    {/* Close Product Details div */}
                    </div>
                    
                    {/* Seller controls for edit mode */}
                    {editMode && (
                      <div style={{ display: 'flex', gap: 8, position: 'absolute', top: 16, left: 16 }}>
                        <button
                          onClick={e => { e.stopPropagation(); handleEditProduct(product.id); }}
                          style={{ background: 'transparent', border: 'none', borderRadius: '50%', padding: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          aria-label="Edit product"
                        >
                          <Edit set="bold" primaryColor="#fff" size={22} />
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); handleDeleteProduct(product.id); }}
                          style={{ background: 'transparent', border: 'none', borderRadius: '50%', padding: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          aria-label="Delete product"
                        >
                          <Delete set="bold" primaryColor="#fff" size={22} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
                );
              })()
        )}
      </section>
      <BottomNavigation />

      {/* Variant Selection Sheet */}
      {showVariantSheet && selectedProduct && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(4px)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            width: '100%',
            maxHeight: '80vh',
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            padding: '20px',
            overflowY: 'auto',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 -10px 30px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20,
              paddingBottom: 15,
              borderBottom: '1px solid #eee'
            }}>
              <h2 style={{ ...headingFont, fontSize: 20, margin: 0 }}>
                {selectedProduct.name}
              </h2>
              <button
                onClick={() => {
                  setShowVariantSheet(false);
                  setSelectedProduct(null);
                  setVariantSelections({});
                  setQuantity(1);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            {/* Product Info */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <img
                  src={selectedProduct.imageUrl || selectedProduct.image}
                  alt={selectedProduct.name}
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 8,
                    objectFit: 'cover'
                  }}
                />
                <div>
                  <div style={{ ...headingFont, fontSize: 16, marginBottom: 4 }}>
                    {selectedProduct.name}
                  </div>
                  <div style={{ ...bodyFont, fontSize: 14, color: '#666' }}>
                    ${selectedProduct.price}
                  </div>
                </div>
              </div>
            </div>

            {/* Variants */}
            {(selectedProduct.variants || selectedProduct.variations || selectedProduct.sizes || selectedProduct.spiceLevels || []).map((variant, index) => (
              <div key={index} style={{ marginBottom: 20 }}>
                <h3 style={{ ...headingFont, fontSize: 16, marginBottom: 10 }}>
                  {variant.name || variant.key}
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {(variant.options || []).map((option, optionIndex) => (
                    <button
                      key={optionIndex}
                      onClick={() => handleVariantSelection(variant.key || variant.name, option.label || option.value)}
                      style={{
                        background: variantSelections[variant.key || variant.name] === (option.label || option.value) 
                          ? 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)' 
                          : '#f5f5f5',
                        color: variantSelections[variant.key || variant.name] === (option.label || option.value) 
                          ? 'white' 
                          : '#333',
                        border: 'none',
                        borderRadius: 8,
                        padding: '8px 16px',
                        fontSize: 14,
                        cursor: 'pointer',
                        ...bodyFont
                      }}
                    >
                      {option.label || option.value}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* Quantity */}
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ ...headingFont, fontSize: 16, marginBottom: 10 }}>
                Quantity
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  style={{
                    background: '#f5f5f5',
                    border: 'none',
                    borderRadius: 8,
                    width: 40,
                    height: 40,
                    fontSize: 18,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  -
                </button>
                <span style={{ ...headingFont, fontSize: 16, minWidth: 30, textAlign: 'center' }}>
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  style={{
                    background: '#f5f5f5',
                    border: 'none',
                    borderRadius: 8,
                    width: 40,
                    height: 40,
                    fontSize: 18,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  +
                </button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCartWithVariants}
              disabled={!allVariantsSelected() || addStatus[selectedProduct.id] === 'adding'}
              style={{
                width: '100%',
                background: allVariantsSelected() 
                  ? (addStatus[selectedProduct.id] === 'added' ? '#10B981' : 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)')
                  : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                padding: '16px',
                fontSize: 16,
                fontWeight: 600,
                cursor: allVariantsSelected() && addStatus[selectedProduct.id] !== 'adding' ? 'pointer' : 'not-allowed',
                ...headingFont
              }}
            >
              {addStatus[selectedProduct.id] === 'added' ? (
                'Added to Cart ✓'
              ) : addStatus[selectedProduct.id] === 'adding' ? (
                'Adding...'
              ) : allVariantsSelected() ? (
                `Add to Cart - $${(selectedProduct.price * quantity).toFixed(2)}`
              ) : (
                'Select Options First'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Product Reviews Drawer */}
      <ProductReviewsDrawer
        isOpen={showReviewsDrawer}
        onClose={handleCloseReviews}
        product={selectedProductForReviews}
        productRating={selectedProductForReviews?.rating || 0}
        productReviewCount={selectedProductForReviews?.reviewCount || 0}
      />
      
      {/* Global styles for drag and drop */}
      <style>{`
        .dragging {
          cursor: grabbing !important;
        }
        .drag-over {
          border: 2px dashed #C97B53 !important;
          opacity: 0.7;
        }
        .drag-handle {
          cursor: grab;
        }
        .drag-handle:active {
          cursor: grabbing;
        }
        
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
}

export default Storefront; 